import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ExpQueryCanvas — the "ask a question, get a temporary board" surface.
 *
 * Flow: the user types a question about the open collection (`AskBox`). We ask
 * the existing `/v1/assistant/collection-chat` endpoint, which answers and
 * names the relevant blocks (`referencedBlockIds`). Those blocks are
 * materialized into a freeform, temporary canvas (`QueryCanvasOverlay`) that
 * shows ONLY the relevant subset.
 *
 * On that canvas the user has three annotation tools:
 *   1. Caption  — pin a short note over a block.
 *   2. Group    — lasso several blocks under a labelled box.
 *   3. Fragment — pull the passage from a block's transcript / scraped HTML
 *                 that's most relevant to the question and show it beside the
 *                 block (extraction is client-side; see `block-artifacts.ts`).
 *
 * All board state is localStorage-backed (`query-canvas.ts`), so a board
 * survives reloads but never leaves the device. No backend changes.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BlockBody, blockCaption, blockLinkUrl, blockMediaUrl, hashHue } from "./block-visuals";
import { extractRelevantFragment, fetchBlockArtifacts } from "./block-artifacts";
import { addCaption, addGroup, clearFragment, deleteCanvas, formatTimestamp, removeBlock, removeCaption, removeGroup, setBlockPosition, setFragment, updateGroupLabel, useQueryCanvas, } from "./canvas-model";
import { useBlockShot, useShowShotsByDefault } from "./urlbox-shots";
import "./query-canvas-overlay.scss";
// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------
const CARD = 200;
const FRAG_W = 240;
const COLS = 3;
/** Column pitch leaves room for a fragment card to the right of each node. */
const COL_PITCH = 540;
const ROW_PITCH = 320;
const ORIGIN = 48;
/** Approx vertical reach of a node above/below its card (caption + meta bar). */
const NODE_CAP = 30;
const NODE_BAR = 26;
/** Initial grid slot for a node before the user drags it. */
function defaultSlot(index) {
    const col = index % COLS;
    const row = Math.floor(index / COLS);
    return { x: ORIGIN + col * COL_PITCH, y: ORIGIN + row * ROW_PITCH };
}
const TOOLS = [
    { tool: "move", glyph: "✣", label: "Arrange", hint: "Drag blocks to arrange them." },
    { tool: "caption", glyph: "💬", label: "Caption", hint: "Click a block to caption it." },
    {
        tool: "group",
        glyph: "⬚",
        label: "Group",
        hint: "Click blocks to select, then name the group.",
    },
    {
        tool: "fragment",
        glyph: "❝",
        label: "Fragment",
        hint: "Click a block to pull the relevant transcript / page passage.",
    },
];
// ---------------------------------------------------------------------------
// Ask box (entry point) — lives in the collection header
// ---------------------------------------------------------------------------
export const AskBox = ({ asking, error, onAsk }) => {
    const [value, setValue] = useState("");
    const submit = () => {
        const q = value.trim();
        if (!q || asking)
            return;
        onAsk(q);
    };
    return (_jsxs("div", { className: "qc-ask", children: [_jsx("input", { type: "text", className: "qc-ask__input", value: value, onChange: (e) => setValue(e.target.value), onKeyDown: (e) => {
                    if (e.key === "Enter")
                        submit();
                }, placeholder: "Ask a question \u2192 build a canvas\u2026", "aria-label": "Ask a question about this collection", disabled: asking }), _jsx("button", { type: "button", className: "qc-ask__btn", onClick: submit, disabled: asking || value.trim().length === 0, children: asking ? "Thinking…" : "Ask" }), error ? _jsx("span", { className: "qc-ask__error", children: error }) : undefined] }));
};
// ---------------------------------------------------------------------------
// Fragment card (tool 3 output)
// ---------------------------------------------------------------------------
/** Friendly label for an artifact type, e.g. "youtube_transcript" → "Transcript". */
function fragmentSourceLabel(artifactType) {
    if (!artifactType)
        return "Source";
    if (artifactType.includes("transcript"))
        return "Transcript";
    if (artifactType === "html_content")
        return "Page";
    if (artifactType === "image_transcription")
        return "Image text";
    return artifactType.replace(/_/g, " ");
}
const FragmentCard = ({ canvasId, blockId, fragment, style }) => {
    const timestamp = formatTimestamp(fragment.startSec);
    return (_jsxs("div", { className: "qc-fragment", style: style, children: [_jsxs("div", { className: "qc-fragment__head", children: [_jsx("span", { className: "qc-fragment__src", children: fragmentSourceLabel(fragment.artifactType) }), timestamp ? _jsxs("span", { className: "qc-fragment__ts", children: ["@ ", timestamp] }) : undefined, _jsx("button", { type: "button", className: "qc-x", onClick: () => clearFragment(canvasId, blockId), "aria-label": "Remove fragment", children: "\u00D7" })] }), fragment.status === "loading" ? (_jsx("div", { className: "qc-fragment__muted", children: "Pulling the relevant passage\u2026" })) : fragment.status === "empty" ? (_jsx("div", { className: "qc-fragment__muted", children: "No transcript or page text to pull from." })) : fragment.status === "error" ? (_jsx("div", { className: "qc-fragment__muted", children: "Couldn\u2019t load this block\u2019s content." })) : (_jsx("blockquote", { className: "qc-fragment__quote", children: fragment.text }))] }));
};
// ---------------------------------------------------------------------------
// Caption editor (tool 1)
// ---------------------------------------------------------------------------
const CaptionEditor = ({ initial, onSave, onCancel, style }) => {
    const [text, setText] = useState(initial);
    const ref = useRef(null);
    useEffect(() => {
        ref.current?.focus();
        ref.current?.select();
    }, []);
    return (_jsxs("div", { className: "qc-capedit", style: style, children: [_jsx("textarea", { ref: ref, className: "qc-capedit__text", value: text, onChange: (e) => setText(e.target.value), onKeyDown: (e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                        onSave(text.trim());
                    if (e.key === "Escape")
                        onCancel();
                }, rows: 2, placeholder: "Caption\u2026" }), _jsxs("div", { className: "qc-capedit__actions", children: [_jsx("button", { type: "button", className: "qc-btn qc-btn--ghost", onClick: onCancel, children: "Cancel" }), _jsx("button", { type: "button", className: "qc-btn", onClick: () => onSave(text.trim()), disabled: text.trim().length === 0, children: "Save" })] })] }));
};
// ---------------------------------------------------------------------------
// Canvas node (one block)
// ---------------------------------------------------------------------------
const CanvasNode = ({ canvas, block, pos, tool, selected, onPointerDown, onPointerMove, onPointerUp, onActivate, }) => {
    const media = blockMediaUrl(block);
    const linkUrl = blockLinkUrl(block);
    const captions = canvas.captions.filter((c) => c.blockId === block.id);
    // Website screenshot (urlbox) support, mirroring the grid tile: once a block
    // has been scraped, show the live screenshot — always when "show by default"
    // is on (the canvas is a screenshot-forward surface), else on hover.
    const shot = useBlockShot(block.id);
    const [showShotsByDefault] = useShowShotsByDefault();
    const [hovered, setHovered] = useState(false);
    const showShot = shot !== undefined && (showShotsByDefault || hovered);
    return (_jsxs("div", { className: `qc-node${selected ? " is-selected" : ""} qc-node--${tool}`, style: { left: pos.x, top: pos.y, width: CARD }, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx("div", { className: "qc-node__card", onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onClick: onActivate, role: "button", tabIndex: 0, children: showShot ? (_jsx("div", { className: "vld-media", children: _jsx("img", { src: shot, alt: block.title ?? "Screenshot", loading: "lazy", draggable: false }) })) : media ? (_jsx("div", { className: "vld-media", children: _jsx("img", { src: media, alt: block.title ?? "", loading: "lazy", draggable: false }) })) : (_jsx(BlockBody, { block: block })) }), _jsxs("div", { className: "qc-node__bar", children: [_jsx("span", { className: "qc-node__name", children: blockCaption(block) }), linkUrl ? (_jsx("a", { className: "qc-node__open", href: linkUrl, target: "_blank", rel: "noreferrer", onClick: (e) => e.stopPropagation(), title: "Open link", children: "\u2197" })) : undefined, _jsx("button", { type: "button", className: "qc-node__remove", onClick: (e) => {
                            e.stopPropagation();
                            removeBlock(canvas.id, block.id);
                        }, title: "Remove from canvas", "aria-label": "Remove from canvas", children: "\u00D7" })] }), captions.length > 0 ? (_jsx("div", { className: "qc-node__captions", children: captions.map((cap) => (_jsxs("span", { className: "qc-caption", children: [_jsx("span", { className: "qc-caption__text", children: cap.text }), _jsx("button", { type: "button", className: "qc-caption__del", onClick: (e) => {
                                e.stopPropagation();
                                removeCaption(canvas.id, cap.id);
                            }, "aria-label": "Delete caption", children: "\u00D7" })] }, cap.id))) })) : undefined] }));
};
// ---------------------------------------------------------------------------
// Group box (tool 2 output)
// ---------------------------------------------------------------------------
const GroupBox = ({ canvasId, group, rect }) => {
    const [editing, setEditing] = useState(false);
    const tint = `hsl(${group.hue} 70% 45%)`;
    return (_jsx("div", { className: "qc-group", style: {
            left: rect.x,
            top: rect.y,
            width: rect.w,
            height: rect.h,
            borderColor: tint,
            background: `hsl(${group.hue} 80% 96% / 0.5)`,
        }, children: _jsxs("div", { className: "qc-group__tab", style: { background: tint }, children: [editing ? (_jsx("input", { className: "qc-group__input", autoFocus: true, defaultValue: group.label, onBlur: (e) => {
                        updateGroupLabel(canvasId, group.id, e.target.value.trim() || group.label);
                        setEditing(false);
                    }, onKeyDown: (e) => {
                        if (e.key === "Enter")
                            e.target.blur();
                    } })) : (_jsx("button", { type: "button", className: "qc-group__label", onClick: () => setEditing(true), children: group.label })), _jsx("button", { type: "button", className: "qc-group__del", onClick: () => removeGroup(canvasId, group.id), "aria-label": "Delete group", children: "\u00D7" })] }) }));
};
// ---------------------------------------------------------------------------
// Auto fragments — pull the relevant snippet for surfaced blocks on open
// ---------------------------------------------------------------------------
/** Cap on how many blocks auto-extract fragments when a canvas first opens. */
const AUTO_FRAGMENT_LIMIT = 18;
/**
 * Pull the relevant fragment for several blocks with bounded concurrency. Marks
 * each loading up front (cards show a spinner), then fills in ready/empty as
 * each block's artifacts come back. The "Fragment" tool the model implicitly
 * uses: asking a question auto-surfaces each block's most-relevant passage.
 */
async function runAutoFragments(canvasId, blockIds, question, answer) {
    for (const id of blockIds)
        setFragment(canvasId, id, { text: "", status: "loading" });
    const CONCURRENCY = 3;
    let cursor = 0;
    const worker = async () => {
        for (;;) {
            const id = blockIds[cursor++];
            if (id === undefined)
                return;
            const artifacts = await fetchBlockArtifacts(id);
            const frag = extractRelevantFragment(artifacts, question, answer);
            if (!frag) {
                setFragment(canvasId, id, { text: "", status: "empty" });
            }
            else {
                setFragment(canvasId, id, {
                    text: frag.text,
                    artifactType: frag.artifactType,
                    startSec: frag.startSec,
                    status: "ready",
                });
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, blockIds.length) }, () => worker()));
}
export const QueryCanvasOverlay = ({ canvasId, blocksById, onClose }) => {
    const canvas = useQueryCanvas(canvasId);
    const [tool, setTool] = useState("move");
    const [selected, setSelected] = useState(new Set());
    const [groupLabel, setGroupLabel] = useState("");
    const [captionFor, setCaptionFor] = useState(undefined);
    const [drag, setDrag] = useState(undefined);
    // Esc closes the board.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    // Resolve the referenced blocks (skip ids we don't have loaded).
    const nodes = useMemo(() => {
        if (!canvas)
            return [];
        return canvas.blockIds
            .map((id, index) => ({ id, index, block: blocksById.get(id) }))
            .filter((n) => n.block !== undefined);
    }, [canvas, blocksById]);
    // Auto-pull fragments once per canvas: when a board first opens, surface the
    // relevant passage for each block that doesn't already have one. The ref keeps
    // this to one run per canvas id even as fragment writes re-render us.
    const autoRanRef = useRef(undefined);
    useEffect(() => {
        if (!canvas || autoRanRef.current === canvas.id)
            return;
        autoRanRef.current = canvas.id;
        const pending = canvas.blockIds
            .filter((id) => blocksById.has(id) && canvas.fragments[id] === undefined)
            .slice(0, AUTO_FRAGMENT_LIMIT);
        if (pending.length === 0)
            return;
        void runAutoFragments(canvas.id, pending, canvas.question, canvas.answer);
    }, [canvas, blocksById]);
    if (!canvas)
        return undefined;
    const posOf = (id, index) => {
        if (drag && drag.blockId === id)
            return { x: drag.x, y: drag.y };
        return canvas.positions[id] ?? defaultSlot(index);
    };
    // --- Drag (move tool) ---
    // Pointer capture routes move/up to the captured node, so all three handlers
    // live on the node card (not the surface).
    const onNodePointerDown = (e, id, index) => {
        if (tool !== "move")
            return;
        e.currentTarget.setPointerCapture(e.pointerId);
        const p = posOf(id, index);
        setDrag({
            blockId: id,
            dx: e.clientX - p.x,
            dy: e.clientY - p.y,
            x: p.x,
            y: p.y,
            moved: false,
        });
    };
    const onNodePointerMove = (e, id) => {
        setDrag((d) => d && d.blockId === id ? { ...d, x: e.clientX - d.dx, y: e.clientY - d.dy, moved: true } : d);
    };
    const onNodePointerUp = (id) => {
        setDrag((d) => {
            if (d && d.blockId === id && d.moved)
                setBlockPosition(canvasId, d.blockId, { x: d.x, y: d.y });
            return undefined;
        });
    };
    // --- Node activation (caption / group / fragment tools) ---
    const onNodeActivate = (block) => {
        if (tool === "caption") {
            setCaptionFor(block.id);
        }
        else if (tool === "group") {
            setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(block.id))
                    next.delete(block.id);
                else
                    next.add(block.id);
                return next;
            });
        }
        else if (tool === "fragment") {
            void pullFragment(block);
        }
    };
    const pullFragment = async (block) => {
        if (canvas.fragments[block.id]) {
            clearFragment(canvasId, block.id);
            return;
        }
        setFragment(canvasId, block.id, { text: "", status: "loading" });
        const artifacts = await fetchBlockArtifacts(block.id);
        const frag = extractRelevantFragment(artifacts, canvas.question, canvas.answer);
        if (!frag) {
            setFragment(canvasId, block.id, { text: "", status: "empty" });
            return;
        }
        setFragment(canvasId, block.id, {
            text: frag.text,
            artifactType: frag.artifactType,
            startSec: frag.startSec,
            status: "ready",
        });
    };
    const createGroup = () => {
        if (selected.size === 0)
            return;
        const label = groupLabel.trim() || `Group of ${selected.size}`;
        addGroup(canvasId, [...selected], label, hashHue(label + Date.now()));
        setSelected(new Set());
        setGroupLabel("");
    };
    // Group bounding boxes from current node positions.
    const groupRects = canvas.groups.map((g) => {
        const members = nodes.filter((n) => g.blockIds.includes(n.id));
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const m of members) {
            const p = posOf(m.id, m.index);
            minX = Math.min(minX, p.x);
            minY = Math.min(minY, p.y - NODE_CAP);
            maxX = Math.max(maxX, p.x + CARD);
            maxY = Math.max(maxY, p.y + CARD + NODE_BAR);
        }
        const pad = 18;
        const rect = members.length > 0
            ? {
                x: minX - pad,
                y: minY - pad - 18,
                w: maxX - minX + pad * 2,
                h: maxY - minY + pad * 2 + 18,
            }
            : { x: 0, y: 0, w: 0, h: 0 };
        return { group: g, rect, visible: members.length > 0 };
    });
    const activeHint = TOOLS.find((t) => t.tool === tool)?.hint ?? "";
    return (_jsxs("div", { className: "qc-overlay", role: "dialog", "aria-modal": "true", "aria-label": "Query canvas", children: [_jsxs("header", { className: "qc-bar", children: [_jsxs("div", { className: "qc-bar__title", children: [_jsx("span", { className: "qc-bar__q", children: canvas.question }), _jsxs("span", { className: "qc-bar__meta", children: [nodes.length, " ", nodes.length === 1 ? "block" : "blocks", " \u00B7 ", canvas.collectionTitle] })] }), _jsx("div", { className: "qc-toolbar", role: "tablist", "aria-label": "Canvas tools", children: TOOLS.map((t) => (_jsxs("button", { type: "button", role: "tab", "aria-selected": tool === t.tool, className: `qc-toolbar__btn${tool === t.tool ? " is-on" : ""}`, onClick: () => {
                                setTool(t.tool);
                                setSelected(new Set());
                                setCaptionFor(undefined);
                            }, title: t.label, children: [_jsx("span", { "aria-hidden": "true", children: t.glyph }), " ", t.label] }, t.tool))) }), _jsxs("div", { className: "qc-bar__right", children: [_jsx("button", { type: "button", className: "qc-btn qc-btn--ghost", onClick: () => {
                                    deleteCanvas(canvasId);
                                    onClose();
                                }, title: "Discard this canvas", children: "Discard" }), _jsx("button", { type: "button", className: "qc-btn", onClick: onClose, children: "Close" })] })] }), canvas.answer ? _jsx("div", { className: "qc-answer", children: canvas.answer }) : undefined, _jsxs("div", { className: "qc-hintbar", children: [_jsx("span", { children: activeHint }), tool === "group" && selected.size > 0 ? (_jsxs("span", { className: "qc-groupmaker", children: [_jsx("input", { className: "qc-groupmaker__input", value: groupLabel, onChange: (e) => setGroupLabel(e.target.value), placeholder: `Name this group of ${selected.size}…`, onKeyDown: (e) => {
                                    if (e.key === "Enter")
                                        createGroup();
                                } }), _jsxs("button", { type: "button", className: "qc-btn", onClick: createGroup, children: ["Group ", selected.size] })] })) : undefined] }), _jsxs("div", { className: "qc-surface", children: [nodes.length === 0 ? (_jsx("div", { className: "qc-empty", children: "The assistant didn\u2019t tie its answer to specific blocks. Try a more specific question." })) : undefined, groupRects.map(({ group, rect, visible }) => visible ? (_jsx(GroupBox, { canvasId: canvasId, group: group, rect: rect }, group.id)) : undefined), nodes.map(({ id, index }) => {
                        const fragment = canvas.fragments[id];
                        if (!fragment)
                            return undefined;
                        const p = posOf(id, index);
                        return (_jsx(FragmentCard, { canvasId: canvasId, blockId: id, fragment: fragment, style: { left: p.x + CARD + 20, top: p.y, width: FRAG_W } }, `frag-${id}`));
                    }), nodes.map(({ id, index, block }) => (_jsx(CanvasNode, { canvas: canvas, block: block, pos: posOf(id, index), tool: tool, selected: selected.has(id), onPointerDown: (e) => onNodePointerDown(e, id, index), onPointerMove: (e) => onNodePointerMove(e, id), onPointerUp: () => onNodePointerUp(id), onActivate: () => onNodeActivate(block) }, id))), captionFor
                        ? (() => {
                            const node = nodes.find((n) => n.id === captionFor);
                            if (!node)
                                return undefined;
                            const p = posOf(node.id, node.index);
                            return (_jsx(CaptionEditor, { initial: "", style: { left: p.x, top: p.y + CARD + NODE_BAR + 8, width: CARD }, onCancel: () => setCaptionFor(undefined), onSave: (text) => {
                                    if (text)
                                        addCaption(canvasId, node.id, text);
                                    setCaptionFor(undefined);
                                } }));
                        })()
                        : undefined] })] }));
};
